import { useEffect, useMemo, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { TabFileError, checkResultFromError, type CanonicalRecord, type CheckIssue } from '@tabservice/tabfile-client';
import { Clock, FilePen, Pencil, RotateCcw, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogClose, DialogContent } from '@/components/ui/dialog';
import { ErrorNote, Skeleton, Spinner } from '@/components/ui/feedback';
import { Pill } from '@/components/ui/pill';
import { useToast } from '@/components/ui/toast';
import { RecordStatusPill } from './status-pill';
import { fixOwnerLabel } from './status';
import { fixMetaFor, fixStateOf, type EFileRecordDetail } from './record-fix';
import {
  fieldsFromSchema,
  isIndicatorOn,
  groupFields,
  valueForInput,
  valueForWire,
  type FormJsonSchema,
  type RecordField,
} from './record-fields';
import { RecordFormSheet, type FieldValue } from './record-form-sheet';
import { filingRecordQuery, formSchemaQuery, queryKeys } from '@/lib/queries';
import { errorMessage, tabfile } from '@/lib/api';
import { formatDateTime } from '@/lib/format';

/** The form state: one entry per field, held as text until it is sent. */
function initialValues(canonical: CanonicalRecord | undefined, fields: RecordField[]): Record<string, FieldValue> {
  const out: Record<string, FieldValue> = {};
  for (const field of fields) {
    const value = (canonical as Record<string, unknown> | undefined)?.[field.name];
    out[field.name] = field.kind === 'indicator' ? isIndicatorOn(value) : valueForInput(field.kind, value);
  }
  return out;
}

/** The IRS result, in the order it is read: what happened, then who fixes it. */
function IrsResult({ record }: { record: EFileRecordDetail }) {
  const errors = record.errors ?? [];
  if (errors.length === 0) {
    /*
     * A record with nothing against it can still be rejected: the IRS refuses a transmission
     * as a whole - a wrong schema version, a fault in the manifest - and every record inside
     * it goes down with it, named or not. Saying only that nothing was reported read as a
     * contradiction of the Rejected badge above, so the chunk's reason is given instead
     * (TS-8532).
     */
    if (record.status === 'rejected') {
      return (
        <div className="rounded-lg border border-[color:var(--ts-border-soft)] bg-blue-bglayout p-4">
          <p className="text-base text-gray-3">
            The IRS reported nothing against this record. It was rejected because the transmission it was filed in
            was rejected as a whole, which takes every record in it down with it.
          </p>
          {record.chunkError ? (
            <p className="mt-2 text-base text-muted-foreground">IRS said: {record.chunkError}</p>
          ) : null}
        </div>
      );
    }
    return <p className="text-base text-muted-foreground">The IRS reported no error against this record.</p>;
  }
  return (
    <ul className="flex flex-col gap-3">
      {errors.map((error, index) => {
        const owner = fixOwnerLabel[error.fixOwner ?? 'unknown'] ?? fixOwnerLabel.unknown;
        return (
          <li
            key={`${record.recordRef}-${error.code ?? index}`}
            className="rounded-lg border border-[color:var(--ts-border-soft)] bg-blue-bglayout p-4"
          >
            <div className="flex flex-wrap items-start justify-between gap-3">
              <p className="text-base text-gray-3">{error.message}</p>
              {owner ? <Pill tone={owner.tone}>{owner.label}</Pill> : null}
            </div>
            {error.irsMessage ? (
              <p className="mt-2 text-base text-muted-foreground">IRS said: {error.irsMessage}</p>
            ) : null}
            {error.code ? <p className="mt-1 text-xs text-gray-1">{error.code}</p> : null}
          </li>
        );
      })}
    </ul>
  );
}

/**
 * The sheet's own shape while it loads: a band, then ruled boxes at the height they will be.
 *
 * A grid of table bars stood here before, which is not what arrives and moved everything
 * under it once the record came back.
 */
function FormSheetSkeleton() {
  return (
    <div className="overflow-hidden rounded-md border border-[color:var(--ts-form-rule)]">
      {[0, 1].map((band) => (
        <div key={band}>
          <div className="bg-[color:var(--ts-form-band)] px-3 py-2">
            <Skeleton className="h-3 w-40 rounded-sm" />
          </div>
          <div className="grid gap-px bg-surface sm:grid-cols-2">
            {[0, 1, 2, 3].map((box) => (
              <div
                key={box}
                className="min-h-[74px] bg-surface px-3 py-2 shadow-[0_0_0_1px_var(--ts-form-rule)]"
              >
                <Skeleton className="h-3 w-24 rounded-sm" />
                <Skeleton className="mt-3 h-4 w-40 rounded-sm" />
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

export interface RecordModalProps {
  filingId: string;
  formType: string | undefined;
  /** The year the e-file was filed for, printed on the sheet as the form prints it. */
  taxYear?: number | string;
  /** How the e-file was sent. Records filed from the customer's own XML are not edited here. */
  format?: string;
  /** The record on screen. Null closes the modal. */
  recordRef: string | null;
  onClose: () => void;
}

/**
 * One record, opened from the records table. It shows what the IRS said, the
 * record's own data as a form, and the three things that can be decided about
 * it: correct it, accept it as it stands, or put it back as it was.
 *
 * Nothing here transmits. Saving queues the fix; the e-file header submits the
 * queue, because the IRS takes a correction as one e-file rather than one call
 * per record.
 */
export function RecordModal({ filingId, formType, taxYear, format, recordRef, onClose }: RecordModalProps) {
  const queryClient = useQueryClient();
  const toast = useToast();

  const record = useQuery(filingRecordQuery(filingId, recordRef));
  const schema = useQuery(formSchemaQuery(formType));

  const [editing, setEditing] = useState(false);
  const [values, setValues] = useState<Record<string, FieldValue>>({});
  const [revealed, setRevealed] = useState<string[]>([]);
  const [issues, setIssues] = useState<CheckIssue[]>([]);
  const [conflict, setConflict] = useState<string | null>(null);

  const data = record.data;
  const fields = useMemo(
    () => fieldsFromSchema(schema.data as FormJsonSchema | undefined, data?.canonical),
    [schema.data, data?.canonical],
  );
  const groups = useMemo(() => groupFields(fields), [fields]);

  // A different record starts clean. An edit in progress is never carried over.
  useEffect(() => {
    setEditing(false);
    setRevealed([]);
    setIssues([]);
    setConflict(null);
  }, [recordRef]);

  useEffect(() => {
    if (!editing) setValues(initialValues(data?.canonical, fields));
  }, [data?.canonical, fields, editing]);

  function refresh() {
    void queryClient.invalidateQueries({ queryKey: ['e-file-records', filingId] });
    void queryClient.invalidateQueries({ queryKey: queryKeys.filing(filingId) });
    if (recordRef) void queryClient.invalidateQueries({ queryKey: queryKeys.filingRecord(filingId, recordRef) });
  }

  /**
   * A 422 names the fields it refused, so it belongs under them. Anything else
   * is about the record as a whole and is said once, above the form.
   */
  function handleFailure(error: unknown, fallbackTitle: string) {
    const result = checkResultFromError(error);
    if (result) {
      setIssues(result.issues);
      setConflict(null);
      return;
    }
    setIssues([]);
    setConflict(errorMessage(error));
    if (!(error instanceof TabFileError) || error.status !== 409) {
      toast.error(fallbackTitle, errorMessage(error));
    }
  }

  const save = useMutation({
    mutationFn: async () => {
      const canonical: Record<string, unknown> = { recordRef: recordRef as string };
      for (const field of fields) {
        // The form and the year are the e-file's, so they are not sent back as edits.
        if (field.pinned) continue;
        canonical[field.name] = valueForWire(field.kind, values[field.name] ?? '');
      }
      return tabfile.PATCH('/v1/e-files/{efileId}/records/{recordRef}', {
        params: { path: { efileId: filingId, recordRef: recordRef as string } },
        body: { canonical: canonical as CanonicalRecord },
      });
    },
    onSuccess: (result) => {
      setEditing(false);
      setIssues([]);
      setConflict(null);
      const word = result.fix?.queuedFor === 'replacement' ? 'replacement' : 'correction';
      toast.success(`Fix queued for ${word}`, 'Submit the queued fixes from the e-file to send them to the IRS.');
      refresh();
      // The decision is made, and what happens next happens on the e-file: the queue is
      // submitted from there. Leaving the record open left the reader looking at a sheet
      // they had just finished with, with the toast about the e-file behind it.
      onClose();
    },
    onError: (error) => handleFailure(error, 'Could not queue the fix'),
  });

  const reopen = useMutation({
    mutationFn: () =>
      tabfile.POST('/v1/e-files/{efileId}/records/{recordRef}/reopen', {
        params: { path: { efileId: filingId, recordRef: recordRef as string } },
      }),
    onSuccess: () => {
      setConflict(null);
      toast.success('Record reopened', 'It is back among the records needing attention.');
      refresh();
    },
    onError: (error) => handleFailure(error, 'Could not reopen the record'),
  });

  const state = data ? fixStateOf(data) : 'none';
  const fixMeta = data ? fixMetaFor(data) : null;
  const submitted = state === 'submitted' || state === 'corrected';
  // What can be decided depends on what the IRS has said. Nothing can be decided about a
  // record the IRS has not answered on, and an accepted record is changed by a correction.
  const awaitingIrs = data ? data.status === 'pending' || data.status === 'transmitted' : true;
  const acceptedByIrs = data?.status === 'accepted';
  const fromOwnXml = format === 'irisxml';
  const canEdit = !awaitingIrs && !fromOwnXml;
  const busy = save.isPending || reopen.isPending;
  const fileLevelIssues = issues.filter((issue) => !issue.field);
  const issueFor = (name: string) => issues.find((issue) => issue.field === name)?.message;

  return (
    <Dialog open={recordRef !== null} onOpenChange={(open) => (open ? undefined : onClose())}>
      <DialogContent
        className="max-w-[1040px]"
        title={data?.recipientName ?? recordRef ?? 'Record'}
        description={data ? `${data.recordRef}, taxpayer ID ${data.recipientTinMasked ?? '***-**-****'}` : undefined}
        header={
          data ? (
            <>
              <RecordStatusPill status={data.status} />
              {fixMeta ? <Pill tone={fixMeta.tone}>{fixMeta.label}</Pill> : null}
              {data.receiptId ? <span className="text-xs text-gray-1">Receipt {data.receiptId}</span> : null}
            </>
          ) : null
        }
        footer={
          data ? (
            <div className="flex w-full flex-wrap items-center justify-end gap-3">
              {(submitted || state === 'fix_failed') && data.fix?.submittedEfileId ? (
                <p className="mr-auto text-base text-muted-foreground">
                  {state === 'fix_failed'
                    ? 'The IRS refused the fix. Make it again on e-file '
                    : state === 'corrected'
                      ? 'Fixed by e-file '
                      : 'Fix sent in e-file '}
                  <Link
                    to="/e-files/$efileId"
                    params={{ efileId: data.fix.submittedEfileId }}
                    onClick={onClose}
                    className="text-link hover:underline"
                  >
                    {data.fix.submittedEfileId}
                  </Link>
                  .
                </p>
              ) : null}


              {editing ? (
                <>
                  <Button variant="outline" disabled={busy} onClick={() => setEditing(false)}>
                    Cancel
                  </Button>
                  <Button variant="primary" disabled={busy} onClick={() => save.mutate()}>
                    {save.isPending ? <Spinner /> : <Send className="h-4 w-4" aria-hidden="true" />}
                    {acceptedByIrs ? 'Save and queue correction' : 'Save and queue fix'}
                  </Button>
                </>
              ) : submitted ? (
                <DialogClose asChild>
                  <Button variant="outline">Close</Button>
                </DialogClose>
              ) : awaitingIrs ? (
                <>
                  <p className="mr-auto flex items-center gap-2 text-base text-muted-foreground">
                    <Clock className="h-4 w-4" aria-hidden="true" />
                    Waiting for the IRS. Fixes open once the IRS has answered.
                  </p>
                  <DialogClose asChild>
                    <Button variant="outline">Close</Button>
                  </DialogClose>
                </>
              ) : acceptedByIrs ? (
                <>
                  <p className="mr-auto text-base text-muted-foreground">
                    {fromOwnXml
                      ? 'Accepted by the IRS. To change it, send a correction in your own XML.'
                      : 'Accepted by the IRS. Changing it queues a correction.'}
                  </p>
                  <DialogClose asChild>
                    <Button variant="outline">Close</Button>
                  </DialogClose>
                  {fromOwnXml ? null : (
                    <Button variant="primary" disabled={busy} onClick={() => setEditing(true)}>
                      <FilePen className="h-4 w-4" aria-hidden="true" />
                      Correct this record
                    </Button>
                  )}
                </>
              ) : (
                <>
                  {fromOwnXml ? (
                    <p className="mr-auto text-base text-muted-foreground">
                      Filed from your own XML. To fix it, send a corrected XML.
                    </p>
                  ) : null}
                  {state === 'none' ? null : (
                    <Button variant="outline" disabled={busy} onClick={() => reopen.mutate()}>
                      {reopen.isPending ? <Spinner /> : <RotateCcw className="h-4 w-4" aria-hidden="true" />}
                      Reopen
                    </Button>
                  )}
                  {canEdit ? (
                    <Button variant="primary" disabled={busy} onClick={() => setEditing(true)}>
                      <Pencil className="h-4 w-4" aria-hidden="true" />
                      Fix record
                    </Button>
                  ) : null}
                </>
              )}
            </div>
          ) : null
        }
      >
        {record.isPending ? (
          <FormSheetSkeleton />
        ) : record.isError ? (
          <ErrorNote title="Could not load this record" detail={errorMessage(record.error)} />
        ) : data ? (
          <div className="flex flex-col gap-6">
            {conflict ? <ErrorNote title="This record cannot be changed" detail={conflict} /> : null}
            {fileLevelIssues.length > 0 ? (
              <ErrorNote
                title="The corrected record did not pass the checks"
                detail={fileLevelIssues.map((issue) => issue.message).join(' ')}
              />
            ) : issues.length > 0 ? (
              <ErrorNote
                title="The corrected record did not pass the checks"
                detail="The fields named below carry what has to change."
              />
            ) : null}

            <section>
              <h3 className="text-base font-medium text-gray-3">IRS result</h3>
              <div className="mt-3">
                <IrsResult record={data} />
              </div>
            </section>

            {data.fix?.note ? (
              <p className="rounded-lg border border-[color:var(--ts-border-soft)] bg-blue-bglayout p-4 text-base text-muted-foreground">
                Note: {data.fix.note}
                {data.fix.updatedBy ? ` (${data.fix.updatedBy}, ${formatDateTime(data.fix.updatedAt)})` : null}
              </p>
            ) : null}

            <section data-tour="record-data">
              <div className="flex flex-wrap items-baseline justify-between gap-2 pb-3">
                <h3 className="text-base font-medium text-gray-3">Record data</h3>
                <p className="text-xs text-muted-foreground">
                  {editing
                    ? 'Taxpayer ids stay masked until a new one is entered.'
                    : canEdit
                      ? 'Read only until edited.'
                      : 'Read only.'}
                </p>
              </div>
              {schema.isPending ? (
                <FormSheetSkeleton />
              ) : groups.length === 0 ? (
                <p className="text-base text-muted-foreground">This record carries no data fields.</p>
              ) : (
                <RecordFormSheet
                  groups={groups}
                  values={values}
                  filedCanonical={data.filedCanonical as Record<string, unknown> | undefined}
                  editing={editing}
                  revealed={revealed}
                  issueFor={issueFor}
                  onChange={(name, next) => setValues((current) => ({ ...current, [name]: next }))}
                  onReveal={(name) => {
                    setRevealed((current) => [...current, name]);
                    setValues((current) => ({ ...current, [name]: '' }));
                  }}
                  heading={
                    <div className="flex flex-wrap items-baseline justify-between gap-3 border-b border-[color:var(--ts-form-rule)] bg-[color:var(--ts-form-band)] px-4 py-3">
                      <p className="text-base font-semibold text-gray-3">
                        {formType ?? 'Form'}
                        {taxYear ? <span className="font-normal text-muted-foreground"> · tax year {taxYear}</span> : null}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Record {data.recordRef}
                        {data.chunk !== undefined ? ` · chunk ${data.chunk}` : ''}
                      </p>
                    </div>
                  }
                />
              )}
            </section>
          </div>
        ) : null}
      </DialogContent>

    </Dialog>
  );
}
